import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { useColorScheme as useSystemColorScheme } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";

/**
 * Theme mode options
 */
export type ThemeMode = "light" | "dark" | "system";

/**
 * Resolved color scheme (actual light/dark)
 */
export type ColorScheme = "light" | "dark";

/**
 * Theme context type
 */
interface ThemeContextType {
  /** Current theme mode setting */
  themeMode: ThemeMode;
  /** Resolved color scheme (what's actually displayed) */
  colorScheme: ColorScheme;
  /** Whether dark mode is active */
  isDark: boolean;
  /** Set the theme mode */
  setThemeMode: (mode: ThemeMode) => Promise<void>;
  /** Toggle between light and dark */
  toggleTheme: () => Promise<void>;
}

const THEME_STORAGE_KEY = "staatsen_theme_mode";

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Theme Provider Props
 */
interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Theme Provider Component
 * Manages dark/light mode with system preference detection and persistence
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  const systemColorScheme = useSystemColorScheme();
  const [themeMode, setThemeModeState] = useState<ThemeMode>("system");
  const [isInitialized, setIsInitialized] = useState(false);

  /**
   * Resolve the actual color scheme based on theme mode and system preference
   */
  const colorScheme: ColorScheme =
    themeMode === "system"
      ? systemColorScheme === "dark"
        ? "dark"
        : "light"
      : themeMode;

  const isDark = colorScheme === "dark";

  /**
   * Load saved theme preference on mount
   */
  useEffect(() => {
    const loadTheme = async () => {
      try {
        const savedTheme = await AsyncStorage.getItem(THEME_STORAGE_KEY);
        if (savedTheme && ["light", "dark", "system"].includes(savedTheme)) {
          setThemeModeState(savedTheme as ThemeMode);
        }
      } catch (error) {
        console.error("Error loading theme:", error);
      } finally {
        setIsInitialized(true);
      }
    };

    loadTheme();
  }, []);

  /**
   * Set and persist theme mode
   */
  const setThemeMode = useCallback(async (mode: ThemeMode) => {
    try {
      await AsyncStorage.setItem(THEME_STORAGE_KEY, mode);
      setThemeModeState(mode);
    } catch (error) {
      console.error("Error saving theme:", error);
    }
  }, []);

  /**
   * Toggle between light and dark (bypasses system)
   */
  const toggleTheme = useCallback(async () => {
    const newMode = isDark ? "light" : "dark";
    await setThemeMode(newMode);
  }, [isDark, setThemeMode]);

  // Don't render until theme is loaded to prevent flash
  if (!isInitialized) {
    return null;
  }

  return (
    <ThemeContext.Provider
      value={{
        themeMode,
        colorScheme,
        isDark,
        setThemeMode,
        toggleTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to use theme context
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

/**
 * Hook to get theme-aware colors
 * Returns appropriate colors based on current theme
 */
export function useThemeColors() {
  const { isDark } = useTheme();

  return {
    // Backgrounds
    background: isDark ? "#0F172A" : "#FFFFFF",
    backgroundSecondary: isDark ? "#1E293B" : "#F9FAFB",
    backgroundTertiary: isDark ? "#334155" : "#F3F4F6",

    // Surfaces (cards, modals)
    surface: isDark ? "#1E293B" : "#FFFFFF",
    surfaceSecondary: isDark ? "#334155" : "#F9FAFB",

    // Text
    text: isDark ? "#F9FAFB" : "#111827",
    textSecondary: isDark ? "#9CA3AF" : "#6B7280",
    textTertiary: isDark ? "#6B7280" : "#9CA3AF",
    textInverse: isDark ? "#111827" : "#F9FAFB",

    // Borders
    border: isDark ? "#374151" : "#E5E7EB",
    borderSecondary: isDark ? "#4B5563" : "#F3F4F6",

    // Primary colors (same in both themes)
    primary: "#7C3AED",
    primaryLight: isDark ? "#4C1D95" : "#EDE9FE",
    primaryText: isDark ? "#C4B5FD" : "#7C3AED",

    // Status colors
    success: "#10B981",
    successLight: isDark ? "#064E3B" : "#D1FAE5",
    warning: "#F59E0B",
    warningLight: isDark ? "#78350F" : "#FEF3C7",
    error: "#EF4444",
    errorLight: isDark ? "#7F1D1D" : "#FEE2E2",

    // Special
    overlay: isDark ? "rgba(0, 0, 0, 0.7)" : "rgba(0, 0, 0, 0.5)",
  };
}

